import * as Sentry from '@sentry/react';
import { OctoPaasEndpointV2, OctopaasExecutor } from 'modules/common/requests/ExecutorFactory';
import { Analytics, MixpanelProperties } from 'modules/common/util/Analytics';
import { getEnumValues } from 'modules/common/util/ObjectUtil';
import { isHttpOnlyCookieAuth } from '../auth/authMode';
import { switchRegion } from '../auth/oauth';
import { session } from '../auth/session';
import {
  CompanyAssociationTypes,
  CompanyRole,
  CompanyRoleDisplay,
  OrganizationBase,
  UserCompany,
  UserCompanyBase,
} from '../models/orgs';

export const assignableCompanyRoles = getEnumValues(CompanyRole).filter(
  (r) => r !== CompanyRole.DSC && r !== CompanyRole.SYSTEM_ACCOUNT
);
export const getCompanyRoleHierarchies = (companyRoles: string[]): number => {
  if (companyRoles) {
    const hasOnlyOneRole = companyRoles.length === 1;
    const hasOnlyTwoRoles = companyRoles.length === 2;
    const isAdmin = companyRoles.includes(CompanyRole.ADMIN);
    const isDeveloper = companyRoles.includes(CompanyRole.DEVELOPER);
    const isSpecialist = companyRoles.includes(CompanyRole.SPECIALIST);
    const isMember = companyRoles.includes(CompanyRole.MEMBER);
    const isDsc = companyRoles.includes(CompanyRole.DSC) || companyRoles.includes(CompanyRoleDisplay.PARTNER);

    if (hasOnlyOneRole && isAdmin) return 0;
    if (hasOnlyTwoRoles && isDsc && isAdmin) return 1;
    if (hasOnlyTwoRoles && isDsc && isDeveloper) return 2;
    if (hasOnlyTwoRoles && isDsc && isSpecialist) return 3;
    if (hasOnlyTwoRoles && isDsc && isMember) return 4;
    if (hasOnlyOneRole && isDsc) return 5;
    if (hasOnlyOneRole && isDeveloper) return 6;
    if (hasOnlyOneRole && isSpecialist) return 7;
    if (hasOnlyOneRole && isMember) return 8;
  }
  return 9;
};

export const isPrimary = <T extends OrganizationBase>(org: T) =>
  org.associationType === CompanyAssociationTypes.PRIMARY;

export const getPrimaryOrg = <T extends OrganizationBase>(orgs: T[]) => orgs?.find((org) => isPrimary(org));

export const sortByPrimaryOrg = <T extends OrganizationBase>(orgs: T[]): T[] =>
  orgs?.sort((org1: OrganizationBase, org2: OrganizationBase) => {
    if (org1.associationType === org2.associationType) {
      return org1.organizationName < org2.organizationName ? -1 : 1;
    }
    if (org1.associationType === CompanyAssociationTypes.PRIMARY) {
      return -1;
    }
    if (org2.associationType === CompanyAssociationTypes.PRIMARY) {
      return 1;
    }
    if (
      org1.associationType === CompanyAssociationTypes.SECONDARY &&
      org2.associationType === CompanyAssociationTypes.COLLABORATOR
    ) {
      return -1;
    }
    if (
      org2.associationType === CompanyAssociationTypes.SECONDARY &&
      org1.associationType === CompanyAssociationTypes.COLLABORATOR
    ) {
      return 1;
    }
    return 0;
  });

const COMPANY_NOT_FOUND_MESSAGE = "Company not found in its region's company list";

/**
 * Fetches the org, switching the session to the org's region first if needed. Tracks `Company Fetch Succeeded`, or
 * `Company Fetch Failed` and rethrows. Callers can tag the events through `analyticsProperties`, which are passed
 * through as is. Analytics include region and auth details but never company names or UUIDs.
 */
export const fetchOrg = async (org: UserCompanyBase, analyticsProperties: MixpanelProperties = {}) => {
  // if the session isn't in the right region, an org cannot be fetched
  const sessionInfo = await session.getInfo();
  const crossRegion = org.region !== sessionInfo.scope.region;
  const properties: MixpanelProperties = {
    ...analyticsProperties,
    crossRegion,
    fromRegion: sessionInfo.scope.region,
    toRegion: org.region,
    authMode: isHttpOnlyCookieAuth(sessionInfo.platform) ? 'Server Token Exchange' : 'Browser Token Exchange',
  };

  try {
    if (crossRegion) {
      await switchRegion(org.region);
    }

    const getOrgsWithinRegion = await OctopaasExecutor.get(OctoPaasEndpointV2.SELF_ORGS);
    const userCompany: UserCompany | undefined = getOrgsWithinRegion.body.userOrganizations.find(
      (o: UserCompany) => o.organizationUuid === org.organizationUuid
    );
    // Callers would otherwise set the current org to undefined
    if (!userCompany) {
      throw new Error(COMPANY_NOT_FOUND_MESSAGE);
    }

    Analytics.track(Analytics.FunctionalArea.GENERAL, 'Company Fetch Succeeded', properties);
    return userCompany;
  } catch (error) {
    Analytics.track(Analytics.FunctionalArea.GENERAL, 'Company Fetch Failed', {
      ...properties,
      ...getFailureProperties(error),
    });
    Sentry.captureException(error, { extra: { message: 'Company Fetch Failed', ...properties } });
    throw error;
  }
};

/**
 * Buckets a failure by whether a response arrived. Blocked CORS responses and network errors carry no status. A
 * company missing from a successful response gets its own bucket so it isn't mistaken for a network failure. Raw
 * errors go to Sentry rather than Mixpanel since SDK errors include request URLs and response headers.
 */
const getFailureProperties = (error?: { message?: string; response?: { status?: number } }): MixpanelProperties => {
  if (error?.message === COMPANY_NOT_FOUND_MESSAGE) return { failureType: 'Company Not Found' };
  const httpStatus = error?.response?.status;
  return httpStatus ? { failureType: 'HTTP Error', httpStatus } : { failureType: 'No Response' };
};
